import { SlingoEvent } from "@lib/titan2-slingo/dist/game/controller/event/SlingoEvent";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { TitanText } from "@lib/titan2/dist/game/control/TitanText";
import { FeatureType } from "../../../model/GameModel";
import { addToTitanSpineSlot, updateResponsiveSpineSlotsChildren } from "../../../utils/SpineUtils";
import { SlingoLang } from "@lib/titan2-slingo/dist/game/view/abstract/lang/SlingoLang";
import { GameLang } from "../lang/GameLang";
import { AbstractGameView } from "@lib/titan2-slingo/dist/game/view/abstract/display/AbstractGameView";
import { EventHandler } from "@lib/titan2/dist/util/event/EventHandler";
/**
 * GameSummary.ts
 */
export class GameSummary extends AbstractGameView {
    create() {
        super.create();
        this.panel = TitanSpine.createSpine(this, this.landscapeProperties.panelSpineProperties, this.portraitProperties?.panelSpineProperties);
        this.titleField = TitanText.createField(this, this.landscapeProperties.title, this.portraitProperties.title);
        this.totalWinTitleField = TitanText.createField(null, this.landscapeProperties.totalWinTitle, this.portraitProperties?.totalWinTitle);
        this.totalWinValueField = TitanText.createField(null, this.landscapeProperties.totalWinValue, this.portraitProperties?.totalWinValue);
        this.slingosWinTitleField = TitanText.createField(null, this.landscapeProperties.slingosWinTitle, this.portraitProperties.slingosWinTitle);
        this.slingosWinValueField = TitanText.createField(null, this.landscapeProperties.slingosWinValue, this.portraitProperties.slingosWinValue);
        this.bonusWinTitleField = TitanText.createField(null, this.landscapeProperties.bonusWinTitle, this.portraitProperties.bonusWinTitle);
        this.bonusWinValueField = TitanText.createField(null, this.landscapeProperties.bonusWinValue, this.portraitProperties?.bonusWinValue);
        this.gameOverTitleField = TitanText.createField(null, this.landscapeProperties.gameOverTitle, this.portraitProperties?.gameOverTitle);
        addToTitanSpineSlot(this.panel, this.titleField, "dynamic-slot-title");
        addToTitanSpineSlot(this.panel, this.gameOverTitleField, "dynamic-slot-title");
        addToTitanSpineSlot(this.panel, this.slingosWinTitleField, "dynamic-slot-label-1");
        addToTitanSpineSlot(this.panel, this.slingosWinValueField, "dynamic-slot-value-1");
        addToTitanSpineSlot(this.panel, this.bonusWinTitleField, "dynamic-slot-label-2");
        addToTitanSpineSlot(this.panel, this.bonusWinValueField, "dynamic-slot-value-2");
        addToTitanSpineSlot(this.panel, this.totalWinTitleField, "dynamic-total-win-label");
        addToTitanSpineSlot(this.panel, this.totalWinValueField, "dynamic-total-win-value");
        this.panel.onComplete(this.summaryComplete, this);
    }
    updateCells() {
        this.result = this.model.getResultData();
        this.panel.visible = false;
        const matchedPatterns = this.model.getMatchedPatterns().size();
        if (this.result.hasSlingoWin()) {
            const currentMatchedPatterns = this.model.getCurrentMatchedPatterns();
            const featureNameMapping = {
                5: this.langManager.getLang(GameLang.SLINGO_5),
                6: this.langManager.getLang(GameLang.SLINGO_6),
                7: this.langManager.getLang(GameLang.SLINGO_7),
                8: this.langManager.getLang(GameLang.SLINGO_8),
                9: this.langManager.getLang(GameLang.SLINGO_9),
                10: this.langManager.getLang(GameLang.SLINGO_10),
                12: this.model.getFeatureType() === FeatureType.FISH_FOOD ? this.langManager.getLang(GameLang.SLINGO_12_2) : this.langManager.getLang(GameLang.SLINGO_12_1),
            };
            this.slingosWinValueField.setText(matchedPatterns.toString(), true);
            this.bonusWinValueField.setText(featureNameMapping[currentMatchedPatterns], true);
            this.totalWinValueField.setText(this.formatCurrency(this.model.getTotalWin()), true);
            this.resizeRowToFit(this.bonusWinTitleField, this.bonusWinValueField);
            this.resizeRowToFit(this.slingosWinTitleField, this.slingosWinValueField);
            this.titleField.setText(this.langManager.getLang(GameLang.RESULT), true);
            this.gameOverTitleField.setText("", true);
        }
        else {
            this.titleField.setText("", true);
            this.gameOverTitleField.setText(this.langManager.getLang(SlingoLang.GAME_OVER), true);
        }
        return this.result.hasSlingoWin() ? 1 : 0;
    }
    resizeRowToFit(titleField, valueField) {
        // force both fields to render unbounded so we can get accurate sizing for both
        titleField.getProperties().size.x = 9999999;
        valueField.getProperties().size.x = 9999999;
        titleField.setText(titleField.getText(), true);
        valueField.setText(valueField.getText(), true);
        const actualTotalWidth = titleField.getPixiText().width + this.uiProperties.cellTitleValuePadding + valueField.getPixiText().width;
        const targetTotalWidth = this.uiProperties.totalCellWidth;
        const scale = actualTotalWidth / targetTotalWidth;
        // if the sum of both fields exceeds the bounds, scale them both down proportionally to make fit
        if (scale > 1) {
            titleField.getProperties().size.x = (titleField.getPixiText().width / scale) - this.uiProperties.cellTitleValuePadding;
            titleField.setText(titleField.getText(), true);
            valueField.getProperties().size.x = valueField.getPixiText().width / scale;
            valueField.setText(valueField.getText(), true);
        }
    }
    play() {
        const wins = this.updateCells();
        if (wins === 0) {
            this.panel.play(`0-GameOver`, false, false, 0);
        }
        else {
            this.panel.play(`0-panel-IN`, false, false, 0);
            this.callAfter(3000, () => {
                this.panel.play(`0-panel-OUT`, false, false, 0);
            });
        }
        this.panel.getSpine().update(0.01);
        this.panel.visible = true;
    }
    summaryComplete() {
        if (this.panel.getTrackEntry(0).animation.name === `0-panel-IN`) {
            return;
        }
        this.callHandler(this.completeHandler);
        this.dispatchGame(SlingoEvent.GAME_SUMMARY_COMPLETE);
    }
    onComplete(handler, scope) {
        this.completeHandler = new EventHandler(handler, scope);
    }
    handleRotate(event) {
        super.handleRotate(event);
        updateResponsiveSpineSlotsChildren(this.panel);
    }
}
//# sourceMappingURL=GameSummary.js.map